import { traduzirErroSupabase } from '@octawash/shared';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  Sparkles,
  User,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase, tenantCodeAtual } from '@/lib/supabase';
import { useTenant } from '@/lib/tenant-context';

/**
 * Calcula força de senha (0..4) com critérios simples e claros.
 */
function calcularForcaSenha(senha: string): { score: number; label: string; cor: string } {
  let score = 0;
  if (senha.length >= 10) score++;
  if (senha.length >= 14) score++;
  if (/[A-Z]/.test(senha) && /[a-z]/.test(senha)) score++;
  if (/\d/.test(senha)) score++;
  if (/[^A-Za-z0-9]/.test(senha)) score++;
  const final = Math.min(score, 4);
  const meta = [
    { label: 'Muito fraca', cor: 'bg-rose-500' },
    { label: 'Fraca', cor: 'bg-rose-400' },
    { label: 'Razoável', cor: 'bg-amber-400' },
    { label: 'Boa', cor: 'bg-emerald-400' },
    { label: 'Excelente', cor: 'bg-emerald-500' },
  ];
  return { score: final, ...meta[final]! };
}

export function SignupPage() {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [nome, setNome] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  const forca = useMemo(() => calcularForcaSenha(senha), [senha]);

  async function cadastrar(e: React.FormEvent) {
    e.preventDefault();
    if (senha.length < 10) {
      toast.error('Senha deve ter ao menos 10 caracteres');
      return;
    }
    setCarregando(true);
    const { error } = await supabase.auth.signUp({
      email,
      password: senha,
      options: {
        data: { cod_tenant: tenantCodeAtual, nom_cliente: nome },
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    setCarregando(false);
    if (error) {
      toast.error('Falha cadastro', { description: traduzirErroSupabase(error) });
      return;
    }
    setSucesso(true);
    toast.success('Cadastro feito! Verifique seu e-mail para confirmar.');
    // Leve delay pra celebração ser percebida antes de redirecionar
    setTimeout(() => navigate('/login', { replace: true }), 1800);
  }

  const nomeMarca = tenant?.nom_tenant ?? 'Octa Pontos';
  const iniciais = tenant?.nom_tenant ? nomeMarca.slice(0, 2).toUpperCase() : 'OW';

  // Tela de sucesso celebratória
  if (sucesso) {
    return (
      <div className="bg-octa-hero relative flex min-h-screen items-center justify-center overflow-hidden p-4">
        <motion.div
          aria-hidden
          className="octa-blob bg-octa-gradient pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem]"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.55, scale: 1 }}
          transition={{ duration: 1.2 }}
        />
        <motion.div
          aria-hidden
          className="octa-blob bg-octa-accent pointer-events-none absolute -bottom-32 -right-32 h-[24rem] w-[24rem]"
          style={{ animationDelay: '-4s' }}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.55, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.15 }}
        />
        <motion.div
          className="relative z-10 w-full max-w-sm text-center"
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <motion.div
            className="bg-octa-gradient shadow-octa-glow mx-auto flex h-24 w-24 items-center justify-center rounded-full ring-4 ring-white/40"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <CheckCircle2 className="h-12 w-12 text-white" />
          </motion.div>
          <motion.h1
            className="text-display-lg mt-6 font-extrabold tracking-tight"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            Bem-vindo a bordo
          </motion.h1>
          <motion.p
            className="text-muted-foreground mt-3 text-sm"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
          >
            Conta criada. Confirme o e-mail para começar.
          </motion.p>
          <motion.div
            className="mt-6 flex items-center justify-center gap-2 text-xs"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85 }}
          >
            <Loader2 className="text-muted-foreground h-3 w-3 animate-spin" />
            <span className="text-muted-foreground">Redirecionando para o login…</span>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-octa-hero relative flex min-h-screen items-center justify-center overflow-hidden p-4">
      {/* Blobs morphing — mesma estética do login (consistência) */}
      <motion.div
        aria-hidden
        className="octa-blob bg-octa-gradient pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem]"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.55, scale: 1 }}
        transition={{ duration: 1.2 }}
      />
      <motion.div
        aria-hidden
        className="octa-blob bg-octa-accent pointer-events-none absolute -bottom-32 -right-32 h-[24rem] w-[24rem]"
        style={{ animationDelay: '-4s' }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.55, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.15 }}
      />
      <motion.div
        aria-hidden
        className="octa-blob pointer-events-none absolute right-1/4 top-1/4 h-72 w-72 bg-violet-400"
        style={{ animationDelay: '-8s', opacity: 0.25 }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.25, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.3 }}
      />

      <div className="relative z-10 w-full max-w-sm space-y-6">
        {/* Logo + boas-vindas */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, scale: 0.85, y: -6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.34, 1.56, 0.64, 1] }}
        >
          {tenant?.des_logo_url ? (
            <img
              src={tenant.des_logo_url}
              alt={nomeMarca}
              className="shadow-octa-glow octa-float mx-auto mb-4 h-20 w-20 rounded-3xl object-cover ring-4 ring-white/40"
            />
          ) : (
            <div className="bg-octa-gradient shadow-octa-glow octa-float mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-3xl ring-4 ring-white/40">
              <span className="text-2xl font-bold text-white">{iniciais}</span>
            </div>
          )}
          <motion.div
            className="mx-auto mb-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-700 ring-1 ring-emerald-500/30 dark:text-emerald-300"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Sparkles className="h-3 w-3" />
            Comece em 2 minutos
          </motion.div>
          <motion.h1
            className="text-display-md font-extrabold tracking-tight"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Criar conta
          </motion.h1>
          <motion.p
            className="text-muted-foreground mt-1.5 text-sm"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            Sua próxima lavagem está a 3 campos de distância.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <Card variant="glass" className="shadow-octa-lg rounded-3xl border-white/40">
            <CardContent className="space-y-5 p-6">
              <form onSubmit={cadastrar} className="octa-stagger space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="nome" className="text-xs font-semibold uppercase tracking-wider">
                    Nome completo
                  </Label>
                  <div className="relative">
                    <User
                      aria-hidden
                      className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                    />
                    <Input
                      id="nome"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      required
                      autoComplete="name"
                      placeholder="Como devemos te chamar?"
                      className="h-11 pl-9"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">
                    E-mail
                  </Label>
                  <div className="relative">
                    <Mail
                      aria-hidden
                      className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                    />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      placeholder="seu@email.com"
                      className="h-11 pl-9"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="senha" className="text-xs font-semibold uppercase tracking-wider">
                    Senha
                  </Label>
                  <div className="relative">
                    <Lock
                      aria-hidden
                      className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                    />
                    <Input
                      id="senha"
                      type={mostrarSenha ? 'text' : 'password'}
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      required
                      minLength={10}
                      autoComplete="new-password"
                      placeholder="Mínimo 10 caracteres"
                      className="h-11 pl-9 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarSenha((v) => !v)}
                      aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                      className="text-muted-foreground hover:text-foreground absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      {mostrarSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {/* Indicador de força — só aparece com >0 chars */}
                  <AnimatePresence>
                    {senha.length > 0 ? (
                      <motion.div
                        className="space-y-1.5 pt-1"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                      >
                        <div className="flex gap-1">
                          {[0, 1, 2, 3].map((i) => (
                            <motion.div
                              key={i}
                              className={`h-1 flex-1 rounded-full transition-colors ${
                                i < forca.score ? forca.cor : 'bg-muted'
                              }`}
                              initial={{ scaleX: 0 }}
                              animate={{ scaleX: 1 }}
                              transition={{ delay: i * 0.05 }}
                              style={{ transformOrigin: 'left' }}
                            />
                          ))}
                        </div>
                        <p className="text-muted-foreground text-[11px] font-medium">
                          Força: <span className="text-foreground">{forca.label}</span>
                        </p>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={carregando}
                  className="octa-cta-magic group h-12 w-full rounded-xl text-base font-bold text-white shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {carregando ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Cadastrando…
                    </>
                  ) : (
                    <>
                      Criar conta
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </Button>
              </form>

              <p className="text-muted-foreground text-center text-xs">
                Já tem conta?{' '}
                <a
                  href="/login"
                  className="text-primary inline-flex items-center gap-1 font-semibold hover:underline"
                >
                  Entrar
                  <ArrowRight className="h-3 w-3" />
                </a>
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.p
          className="text-muted-foreground text-center text-[11px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.75, duration: 0.5 }}
        >
          Ao criar sua conta você concorda com nossos termos e a política de privacidade.
        </motion.p>
      </div>
    </div>
  );
}
